<template>
  <div>
    <h3>Advanced Tags</h3>
    <div>
      <form v-if="!showNewRuleForm" @submit.prevent="submitted">
        <div class="form-group">
          <div>
            <drop-down
              v-model="contentControlType"
              id="content-control-type"
              name="content-control-type"
              label="Type"
              :items="typeListItems"
            />
            <text-field
              v-if="showSystemNameField"
              v-model="v$.systemName.$model"
              id="system-name"
              label="System Name"
              placeholder="Enter the Sharedo system name here."
              mandatory
            >
              <template v-slot:validation v-if="v$.systemName.$error">
                <validation-error v-if="!v$.systemName.required"
                  >You must enter a system name.</validation-error
                >
              </template>
            </text-field>
            <rule-selector
              v-if="showRuleSelector && currentTemplates.length > 0"
              v-model="businessRuleId"
              @new-rule="showNewRuleForm = true"
            />
          </div>
        </div>
        <form-buttons>
          <button class="btn btn-primary">Add</button>
        </form-buttons>
      </form>
      <div v-if="showNewRuleForm">
        <rule-form @save="newRuleCreated" @cancel="showNewRuleForm = false" />
      </div>
    </div>
  </div>
</template>
<script>
import { mapState } from "vuex";
import { required } from "@vuelidate/validators";
import { useVuelidate } from "@vuelidate/core";
import documentModelMixin from "@/mixins/documentModel";
import currentTempates from "@/mixins/currentTemplates";
import RuleSelector from "@/components/business-rules/RuleSelector.vue";
import RuleForm from "@/components/business-rules/RuleForm.vue";
import {
  DICTATION_CONTENT_BLOCK,
  TAG,
  CONTENT_BLOCK,
} from "@/constants/contentControlTypes";
import {
  DICTATION_OUTPUT,
  DictationBlock,
  Tag,
  ContentBlock,
} from "@/models/documentModel";
import dicationBlocks from "@/services/word/dictationBlocksService";
import tags from "@/services/word/tagsService";
import contentBlockTags from "@/services/word/contentBlockTagsService";

const newRuleCreated = function (newRule) {
  this.businessRuleId = newRule.id;
  this.showNewRuleForm = false;
};

const updateDocumentModel = async function (contentControl, parentId) {
  this.logDebug("Saving document model.");
  const model = this.documentModel;

  if (parentId) {
    const parent = model.findContentControl(parentId);
    parent.contentControls.push(contentControl);
  } else {
    model.contentControls.push(contentControl);
  }

  await this.saveDocumentModel(model);
  this.logDebug("Document model saved", model);
};

const addDictationBlock = async function () {
  this.logDebug("Adding dication block.");
  const { id, parentId } = await dicationBlocks.add(DICTATION_OUTPUT, this.boundingBoxesHidden);
  const dictationBlock = new DictationBlock(id);

  await this.updateDocumentModel(dictationBlock, parentId);
  this.logDebug("Dictation block added.", { parentId, dictationBlock });
};

const addTag = async function () {
  this.logDebug("Adding freeform tag.");
  const { id, parentId } = await tags.add(
    this.systemName,
    this.boundingBoxesHidden
  );
  const tag = new Tag(id, this.systemName, this.businessRuleId);

  await this.updateDocumentModel(tag, parentId);
  this.logDebug("Freeform tag added.", { parentId, tag });
};

const addContentBlock = async function () {
  this.logDebug("Adding content block.");
  const { id, parentId } = await contentBlockTags.add(this.systemName, "", this.boundingBoxesHidden);
  const contentBlock = new ContentBlock(
    id,
    this.systemName,
    "",
    null,
    this.businessRuleId
  );

  await this.updateDocumentModel(contentBlock, parentId);
  this.logDebug("Content block added.", { parentId, contentBlock });
};

const submitted = async function () {
  this.v$.$touch();

  if (this.v$.$invalid) {
    this.submitStatus = "ERROR";
  } else {
    this.submitStatus = "PENDING";
    switch (this.contentControlType) {
      case DICTATION_CONTENT_BLOCK:
        await this.addDictationBlock();
        break;
      case TAG:
        await this.addTag();
        break;
      case CONTENT_BLOCK:
        await this.addContentBlock();
        break;
    }

    this.clear();
  }
};

const clear = function () {
  (this.contentControlType = DICTATION_CONTENT_BLOCK),
    (this.systemName = DICTATION_OUTPUT),
    (this.businessRuleId = ""),
    (this.showNewRuleForm = false);
};

export default {
  setup() {
    return { v$: useVuelidate() }
  },
  components: { RuleSelector, RuleForm },
  mixins: [documentModelMixin, currentTempates],
  data: function () {
    return {
      contentControlType: DICTATION_CONTENT_BLOCK,
      systemName: DICTATION_OUTPUT,
      businessRuleId: "",
      showNewRuleForm: false,
    };
  },
  computed: {
    ...mapState({
      boundingBoxesHidden: (state) => state.boundingBoxesHidden,
    }),
    typeListItems: function () {
      return [
        {
          label: "Dictation Block",
          value: DICTATION_CONTENT_BLOCK,
        },
        {
          label: "Tag",
          value: TAG,
        },
        {
          label: "Content Block",
          value: CONTENT_BLOCK,
        },
      ];
    },
    showSystemNameField: function () {
      return this.contentControlType !== DICTATION_CONTENT_BLOCK;
    },
    showRuleSelector: function () {
      return this.contentControlType !== DICTATION_CONTENT_BLOCK;
    },
  },
  validations() {
    return {
      systemName: {
        required,
      },
    };
  },
  watch: {
    contentControlType: function (newValue) {
      if (newValue === DICTATION_CONTENT_BLOCK) {
        this.systemName = DICTATION_OUTPUT;
      } else {
        this.systemName = "";
      }
    },
  },
  methods: {
    submitted,
    addDictationBlock,
    addContentBlock,
    addTag,
    newRuleCreated,
    updateDocumentModel,
    clear,
  },
};
</script>
